import React, { useState, useEffect } from 'react';
import { Settings, Download, Save } from 'lucide-react';

interface DownloadSettingsProps {
  onClose: () => void;
}

const DownloadSettings: React.FC<DownloadSettingsProps> = ({ onClose }) => {
  const [concurrentDownloads, setConcurrentDownloads] = useState(32);
  const [maxConnectionsPerHost, setMaxConnectionsPerHost] = useState(32);
  const [timeout, setTimeout] = useState(120000);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (window.api) {
        try {
          const settings = await window.api.getDownloadSettings();
          setConcurrentDownloads(settings.concurrentDownloads);
          setMaxConnectionsPerHost(settings.connectionsPerHost);
          if (settings.timeout) {
            setTimeout(settings.timeout);
          }
        } catch (error) {
          console.error('Failed to load download settings:', error);
        }
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    if (window.api) {
      setIsSaving(true);
      try {
        const result = await window.api.updateDownloadSettings({
          concurrentDownloads,
          connectionsPerHost: maxConnectionsPerHost,
          timeout
        });

        if (result.success) {
          setSaveSuccess(true);
          setTimeout(() => {
            setSaveSuccess(false);
          }, 2000);
        }
      } catch (error) {
        console.error('Failed to save download settings:', error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="glass-dark border border-white/10 rounded-lg p-6 w-full max-w-md">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center">
          <Download size={20} className="mr-2" />
          Download Settings
        </h2>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded"
        >
          ×
        </button>
      </div>

      <p className="text-sm text-gray-400 mb-4">
        Adjust these settings to optimize download speed. Lower values are more stable but slower, higher values may improve speed but could overwhelm your network.
      </p>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Concurrent Downloads ({concurrentDownloads})
          </label>
          <input
            type="range"
            min="1"
            max="128"
            step="1"
            value={concurrentDownloads}
            onChange={(e) => setConcurrentDownloads(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>64</span>
            <span>128</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Recommended: 32-64. How many files to download at once.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Connections Per Host ({maxConnectionsPerHost})
          </label>
          <input
            type="range"
            min="1"
            max="64"
            step="1"
            value={maxConnectionsPerHost}
            onChange={(e) => setMaxConnectionsPerHost(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>1</span>
            <span>32</span>
            <span>64</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Recommended: 24-48. Maximum connections to each server.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Request Timeout (seconds)
          </label>
          <select
            value={timeout}
            onChange={(e) => setTimeout(parseInt(e.target.value))}
            className="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"
          >
            <option value={60000}>60 seconds</option>
            <option value={120000}>120 seconds</option>
            <option value={180000}>180 seconds</option>
            <option value={240000}>240 seconds</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">
            Recommended: 120 seconds. Increase if you're experiencing timeouts.
          </p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-4 py-2 rounded-lg ${
            saveSuccess 
              ? 'bg-green-600' 
              : 'bg-blue-600 hover:bg-blue-700'
          } transition-colors flex items-center`}
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          ) : saveSuccess ? (
            <Save size={16} className="mr-2" />
          ) : (
            <Settings size={16} className="mr-2" />
          )}
          {saveSuccess ? 'Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default DownloadSettings;